import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer, Table, Button, Typography,
} from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { openModal } from '../../actions/modal';
import ErrorDisplay from '../viewer/errorDisplay';
import './replyMessageSidebar.scss';
import { getReferralsQuery, getReferralReplyUrl } from '../../actions/records';

class ReferralReplySidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: true,
      resentReplies: false,
    };
  }

  handleResend = async (record) => {
    this.setState({ resentReplies: true })
    const {
      original_send_token: sendToken,
      file_token: fileToken,
      patient_name: patientName,
      patient_first_name: patientFirstName,
      patient_middle_name: patientMiddleName,
      patient_last_name: patientLastName,
      patient_dob: patientDOB,
      receiver_fax: receiverFax,
      referring_provider: referringProvider,
      referral_template: referralTemplate,
      slug: slug,
      owner_type: ownerType,
      owner_id: ownerId,
    } = record;

    const [year, month, day] = patientDOB.split('-');
    const formattedDOB = `${month}/${day}/${year}`;

    const modalType = {
      type: 'REFERRAL_REPLY',
      data: {
        caller_name: receiverFax,
        fileToken,
        patientDOB: formattedDOB,
        patientName,
        patientFirstName,
        patientMiddleName,
        patientLastName,
        resentReplySlug: slug,
        resentRecordSlug: record.reply_file_token,
        referringProvider,
        sendToken,
        referralTemplate,
        referralReplyUri: record.referral_reply_uri,
        appointmentDate: record.appointment_date,
        ownerType,
        ownerId,
      },
    };
    this.props.openModal(modalType);
  }

  getUrl = async (url) => {
    const { getReferralReplyUrl } = this.props;
    await getReferralReplyUrl(url);
  }

  onClose = () => {
    const { location } = this.props;

    if (this.state.resentReplies) {
      this.props.getReferralsQuery(location.search);
      this.setState({ resentReplies: false });
    }
    this.props.toggleReferralReplySidebar();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.attachmentUrl &&
      this.props.attachmentUrl !== prevProps.attachmentUrl
    ) {
      window.open(this.props.attachmentUrl);
    }
  }

  render() {
    const { error, data } = this.props;
    const { referral_replies: replies } = data;

    const hasFailedReplies = replies?.some(reply => reply.fax_status === 'failure');
    // Only when a failure reason is shown do the columns make room for it, so
    // the table keeps today's layout otherwise.
    const hasFailureReasons = replies?.some(reply => reply.fax_status === 'failure' && reply.fax_error_message);
    const statusLabels = {
      'success': 'Success',
      'failure': 'Failed', 
      'inprogress': 'In Progress',
      'resent': 'Resent'
    };
    
    const columns = [
      {
        title: 'To Number',
        dataIndex: 'receiver_fax',
        key: 'slug',
        width: hasFailureReasons ? '18%' : '25%',
      },
      {
        title: 'Reply',
        dataIndex: 'sender_fax',
        render: (text, record) => {
          return (
            <>
              <Button icon={<PaperClipOutlined />} type="dashed" onClick={() => this.getUrl(record.record_uri)}>
                Document
              </Button>
            </>
          );
        },
      },
      {
        title: 'Fax Status',
        dataIndex: 'fax_status',
        ...(hasFailureReasons ? { width: '24%' } : {}),
        render: (status, record) => {
          const label = statusLabels[status] || status;
          // Replies carry the failure reason once the server sends it (ENG-3544);
          // without it the cell renders exactly as before.
          if (status !== 'failure' || !record.fax_error_message) return label;
          return (
            <>
              <div>{label}</div>
              <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                {record.fax_error_message}
              </Typography.Text>
            </>
          );
        },
      },
      {
        title: 'Transmission Date',
        dataIndex: 'fax_completed_at',
        width: hasFailureReasons ? '20%' : '25%',
      },
      ...(hasFailedReplies ? [{
        title: 'Action',
        key: 'action',
        render: (_, record) => (
          record.fax_status === 'failure' && (
            <Button 
              type="primary" 
              onClick={() => this.handleResend(record)}
            >
              Resend
            </Button>
          )
        ),
      }] : []),
    ];

    return (
      <Drawer
        title="Referral Replies"
        placement="right"
        closable={true}
        onClose={this.onClose}
        visible={this.state.visible}
        width={730}
        style={{ marginTop: 60, padding: 0 }}
      >
        <div>
          {error ? <ErrorDisplay sidebar={true} message="Error retrieving reply. Please try again" />
            : (
              <>
                <div style={{ borderTop: '1px solid lightgrey', padding: '25px' }}>
                  <Table
                    dataSource={replies}
                    columns={columns}
                    rowKey="slug"
                  />
                </div>
              </>
            )
          }
        </div>
      </Drawer>
    );
  }
}

ReferralReplySidebar.defaultProps = {
  data: {},
  error: false,
  attachmentUrl: null,
  referralReplies: {},
};

ReferralReplySidebar.propTypes = {
  error: PropTypes.bool,
  data: PropTypes.object,
  getReferralReplyUrl: PropTypes.func,
  attachmentUrl: PropTypes.string,
  getReferralReplies: PropTypes.func,
  referralReplies: PropTypes.object,
  toggleReferralReplySidebar: PropTypes.func,
  getReferralsQuery: PropTypes.func.isRequired,
};

export default withRouter(connect(state => ({
  referralReplies: state.records.referralReplies,
  attachmentUrl: state.records.attachmentUrl,
  error: state.records.error,
}), {
  openModal,
  getReferralReplyUrl,
  getReferralsQuery,
})(ReferralReplySidebar));
